import { Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ClientEvideForm, ClientInputContent, IClientEvideFormSaveAnswerHttp, IClientInputContentRequestHttp } from 'client/models';
import { onErrorAlert } from 'common/helper';
import { BehaviorSubject, Observable, Subscription, catchError, finalize, of, tap } from 'rxjs';

import { ToastrService } from 'ngx-toastr';
import { environment } from '../../environments/environment';
import { FormStore } from '../common/form.store';
import { EEvent, TPostMessage } from '../core/Events';
import { ParentWindowService } from '../core/parent-window.service';
import { ClientEvideFormService } from '../services/client-evide-form.service';
import { prepareSaveInputContentsRequest } from './../common/form.helper';

@Component({
  selector: 'client-form',
  templateUrl: './client-form.component.html',
  styleUrls: ['client-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class ClientFormComponent implements OnInit {
  @ViewChild('container') container: ElementRef;

  @Input() set baseApiUrl(apiUrl: string | undefined) {
    if (apiUrl) {
      environment.baseUrl = apiUrl;
    }
  }

  @Input() set baseFormController(controller: string | undefined) {
    if (controller) {
      environment.clientFormController = controller;
    }
  }

  @Input() token: string | undefined;
  @Input() preview = false;

  public clientFormDataObs!: Observable<ClientEvideForm>;
  public clientInputContents: ClientInputContent[] = [];
  public captchaSiteKeyObs!: Observable<string>;

  private clientToken?: string | null = null;
  private formTitle = '';
  private formGuid?: string | null = null;
  public isValid$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public processing$ = new BehaviorSubject<boolean>(false);

  private subs = new Subscription();

  constructor(
    route: ActivatedRoute,
    private router: Router,
    private alert: ToastrService,
    private parentWindow: ParentWindowService,
    private clientFormService: ClientEvideFormService
  ) {
    this.clientToken = route.snapshot.queryParamMap.get('formurl'); // For client
    this.formGuid = route.snapshot.paramMap.get('guid'); // For preview

    const sub = FormStore.resize$.subscribe(this.onResize.bind(this));
    this.subs.add(sub);
  }

  ngOnInit() {
    this.init();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  private init() {
    // A fresh form: forget the previous submission's copy and rule labels.
    FormStore.lastSubmission = null;
    FormStore.ruleOptionLabels = {};
    if (this.clientToken) {
      FormStore.token = this.clientToken;
      this.getFormDataFromUrl();
    } else if (this.formGuid && this.token) {
      FormStore.token = this.token;
      this.getFormDataFromGuid(this.formGuid);
    } else {
      this.isValid$.next(false);
    }
  }


  private getFormDataFromUrl() {
    this.clientFormDataObs = this.clientFormService.getClientFormInfo().pipe(
      tap(res => this.prepareClientInputContents(res)),
      catchError(this.onGetError.bind(this)),
      finalize(() => {
        FormStore.resize$.next();
      })
    );
  }

  private getFormDataFromGuid(guid: string) {
    this.clientFormDataObs = this.clientFormService.getClientFormInfoFormGuid(guid).pipe(
      tap(res => this.prepareClientInputContents(res)),
      catchError(this.onGetError.bind(this)),
    );
  }

  private prepareClientInputContents(clientFormData: ClientEvideForm) {
    this.formTitle = clientFormData.title ?? '';
    this.clientInputContents = [];
    clientFormData.inputContentGroups.forEach(group => group.inputContents.forEach(inputContent => {
      this.clientInputContents.push(inputContent);
    }))
  }

  private onGetError() {
    this.isValid$.next(false);
    return of();
  }

  public submit(formObj: any) {
    if (!formObj) {
      return;
    }
    const body = this.prepareBody(formObj);
    if (!body || !this.clientToken) { return; }
    this.processing$.next(true);

    this.clientFormService.saveClientFormAnswer(body).pipe(
      finalize(() => this.processing$.next(false))
    ).subscribe({
      next: (res) => {
        // Download PDF on the success page renders exactly what was just submitted (memory only).
        FormStore.lastSubmission = {
          formTitle: this.formTitle,
          clientInputContents: body.clientInputContents,
          optionLabels: { ...FormStore.ruleOptionLabels }
        };
        FormStore.reset$.next();
        const state = { successMessage: res.message };
        this.router.navigateByUrl('/success', { state });
      },
      error: (err) => onErrorAlert(this.alert, err)
    });
  }

  private prepareBody(formObj: any): IClientEvideFormSaveAnswerHttp | null {
    if (!this.clientToken) { return null; }

    const clientInputContents: IClientInputContentRequestHttp[] = prepareSaveInputContentsRequest(formObj, this.clientInputContents);
    const request: IClientEvideFormSaveAnswerHttp = {
      clientInputContents,
      encodedResponse: formObj.recaptcha,
      // Reference field untouched (still the provisional) → server allocates the real reference on submit.
      forceGenerateReference: formObj.forceGenerateReference
    };
    return request;
  }

  private onResize() {
    setTimeout(() => {
      this.resizeContainerHeight();
    }, 100);
  }

  // Publish message to iframe saying that it's height changed
  private resizeContainerHeight() {
    const message: TPostMessage = {
      name: EEvent.EvideFormHeightChange,
      data: {
        height: this.container?.nativeElement.scrollHeight
      }
    };
    this.parentWindow.postMessage(message);
  }

}
